import { useMutation } from '@tanstack/react-query';
import { orpc, useInvalidateRpcQueryFn } from '@/api/rpc/client';
import type {
  AcceptMatchesAPInvoiceInput,
  AcceptMatchesAPInvoiceResponse,
} from '@erp/rpc';
import { useToast } from '@/context/ToastContext';

export interface UseAcceptAPInvoiceOptions {
  onSuccess?: (
    data: AcceptMatchesAPInvoiceResponse,
    variables: AcceptMatchesAPInvoiceInput,
  ) => void;
}

export function useAcceptAPInvoice(options?: UseAcceptAPInvoiceOptions) {
  const invalidateRpcQueryFn = useInvalidateRpcQueryFn(
    (orpc, { id }: { id: string }) => [
      orpc.accountsPayable.invoices.get.queryKey({ input: { id } }),
      orpc.accountsPayable.invoices.list.queryKey({ input: {} }),
    ],
  );
  const { showToast } = useToast();

  return useMutation(
    orpc.accountsPayable.invoices.acceptMatches.mutationOptions({
      onSuccess: (data, variables) => {
        showToast({
          title: 'Success',
          message: 'Matches accepted successfully',
          color: 'success',
          icon: 'check',
        });

        invalidateRpcQueryFn({ id: variables.id });

        options?.onSuccess?.(data, variables);
      },
      onError: (error) => {
        showToast({
          title: 'Error',
          message: error.message || 'Failed to accept matches',
          color: 'error',
          icon: 'x',
        });
      },
    }),
  );
}
